// src/components/TrophyRoom/InviteDialog.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  Paper,
  IconButton,
  Alert
} from '@mui/material';
import {
  Business as BusinessIcon,
  Star as StarIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  Twitter as TwitterIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import client from '../../API';

function InviteDialog({ open, onClose, onInviteGenerated }) {
  const [accountType, setAccountType] = useState('brand');
  const [loading, setLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await client.globalUsers.generateInvite({ accountType });
      
      if (result.success && result.invite_url) {
        setGeneratedLink(result.invite_url);
        onInviteGenerated(result.invite_url, accountType);
      } else {
        throw new Error(result.error || 'Failed to generate invite link');
      }
    } catch (error) {
      console.error('Error generating invite:', error);
      setError(error.message || 'Failed to generate invite link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleShare = (platform) => {
    const message = `Join me on Blitz! ${accountType === 'brand' ? 'Connect with creators' : 'Start earning from your content'}.`;
    
    switch (platform) {
      case 'email':
        window.open(`mailto:?subject=Join Blitz&body=${encodeURIComponent(`${message}\n\n${generatedLink}`)}`);
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${message} ${generatedLink}`)}`);
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(`${message} ${generatedLink}`)}`);
        break;
    }
  };

  const handleClose = () => {
    setGeneratedLink('');
    setError('');
    setAccountType('brand');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Generate Invite Link
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {!generatedLink ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Choose what type of account you want to invite people to create:
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
                label="Account Type"
              >
                <MenuItem value="brand">Brand Account - For companies looking to hire creators</MenuItem>
                <MenuItem value="creator">Creator Account - For content creators looking to monetize</MenuItem>
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  border: accountType === 'brand' ? '2px solid' : '1px solid',
                  borderColor: accountType === 'brand' ? 'primary.main' : 'divider',
                  cursor: 'pointer'
                }} onClick={() => setAccountType('brand')}>
                  <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="subtitle1" fontWeight="bold">Brand Account</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hire creators and run campaigns
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  border: accountType === 'creator' ? '2px solid' : '1px solid',
                  borderColor: accountType === 'creator' ? 'primary.main' : 'divider',
                  cursor: 'pointer'
                }} onClick={() => setAccountType('creator')}>
                  <StarIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="subtitle1" fontWeight="bold">Creator Account</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monetize content and get hired
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your invite link has been generated! Share it with anyone you want to invite:
            </Typography>
            
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                {generatedLink}
              </Typography>
            </Paper>

            <Typography variant="subtitle2" sx={{ mb: 2 }}>Share via:</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={handleCopy}
                  sx={{ borderRadius: 2 }}
                >
                  Copy
                </Button>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={() => handleShare('email')}
                  sx={{ borderRadius: 2 }}
                >
                  Email
                </Button>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<TwitterIcon />}
                  onClick={() => handleShare('twitter')}
                  sx={{ borderRadius: 2 }}
                >
                  Twitter
                </Button>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => handleShare('whatsapp')}
                  sx={{ borderRadius: 2 }}
                >
                  WhatsApp
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!generatedLink ? (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            {loading ? 'Generating...' : 'Generate Invite Link'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => {
              setGeneratedLink('');
              setAccountType('brand');
            }}
            sx={{ borderRadius: 2 }}
          >
            Generate Another
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default InviteDialog;